import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { BaseGameProps } from "../common/types";

// Renk paleti
const CARD_COLORS = [
  "bg-red-500",
  "bg-blue-500",
  "bg-green-500",
  "bg-yellow-500",
  "bg-purple-500",
  "bg-pink-500",
  "bg-indigo-500",
  "bg-orange-500",
];

interface Card {
  id: number;
  color: string;
  isFlipped: boolean;
  isMatched: boolean;
}

export function MemoryGame({
  className = "",
  difficulty = "medium",
  onGameEnd,
}: BaseGameProps) {
  const { t } = useTranslation();
  // Zorluk seviyesine göre kart sayısını belirle
  const getCardCount = () => {
    switch (difficulty) {
      case "easy":
        return 8; // 4 çift
      case "medium":
        return 12; // 6 çift
      case "hard":
        return 16; // 8 çift
      default:
        return 12;
    }
  };

  const [cards, setCards] = useState<Card[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [matchedPairs, setMatchedPairs] = useState<number>(0);
  const [moves, setMoves] = useState<number>(0);
  const [gameOver, setGameOver] = useState<boolean>(false);
  const [startTime, setStartTime] = useState<number>(0);
  const [endTime, setEndTime] = useState<number>(0);

  // Oyun tahtasını oluştur
  const initializeGame = () => {
    const cardCount = getCardCount();
    const pairCount = cardCount / 2;

    // Kart çiftlerini oluştur
    let cardPairs: Card[] = [];
    for (let i = 0; i < pairCount; i++) {
      // Her çift için aynı rengi kullan
      const color = CARD_COLORS[i % CARD_COLORS.length];

      // İki adet kart ekle (çift)
      cardPairs.push({
        id: i * 2,
        color,
        isFlipped: false,
        isMatched: false,
      });

      cardPairs.push({
        id: i * 2 + 1,
        color,
        isFlipped: false,
        isMatched: false,
      });
    }

    // Kartları karıştır
    cardPairs = shuffleCards(cardPairs);

    setCards(cardPairs);
    setFlippedCards([]);
    setMatchedPairs(0);
    setMoves(0);
    setGameOver(false);
    setStartTime(Date.now());
    setEndTime(0);
  };

  // Kartları karıştır (Fisher-Yates algoritması)
  const shuffleCards = (cards: Card[]): Card[] => {
    const shuffled = [...cards];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Kartı çevir
  const flipCard = (index: number) => {
    // Eğer zaten 2 kart çevrilmişse, başka kart çevirme
    if (flippedCards.length === 2) return;

    // Eğer kart zaten eşleştirilmişse veya çevrilmişse, işlem yapma
    if (cards[index].isMatched || flippedCards.includes(index)) return;

    // Kartı çevir
    const newFlippedCards = [...flippedCards, index];
    setFlippedCards(newFlippedCards);

    // Eğer 2 kart çevrildiyse, kontrol et
    if (newFlippedCards.length === 2) {
      // Hamle sayısını artır
      setMoves(moves + 1);

      // Eşleşme kontrolü
      const [firstCardIndex, secondCardIndex] = newFlippedCards;
      if (cards[firstCardIndex].color === cards[secondCardIndex].color) {
        // Eşleşme var
        const newCards = [...cards];
        newCards[firstCardIndex].isMatched = true;
        newCards[secondCardIndex].isMatched = true;
        setCards(newCards);

        // Eşleşen çift sayısını artır
        const newMatchedPairs = matchedPairs + 1;
        setMatchedPairs(newMatchedPairs);

        // Tüm çiftler eşleşti mi?
        if (newMatchedPairs === cards.length / 2) {
          setGameOver(true);
          setEndTime(Date.now());
          if (onGameEnd) onGameEnd("win");
        }

        // Kartları çevrilmiş bırak
        setFlippedCards([]);
      } else {
        // Eşleşme yok, kısa süre sonra kartları geri çevir
        setTimeout(() => {
          setFlippedCards([]);
        }, 600);
      }
    }
  };

  // Süreyi formatla
  const formatTime = (milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  // Oyunu başlat
  useEffect(() => {
    initializeGame();
  }, [difficulty]);

  // Kolon sayısını belirle
  const getColumnCount = () => {
    const cardCount = cards.length;
    if (cardCount <= 8) return 4; // 2x4
    if (cardCount <= 12) return 4; // 3x4
    return 4; // 4x4
  };

  return (
    <div className={cn("flex h-full min-h-0 flex-col px-3 py-3 sm:px-4", className)}>
      <div className="mb-3 flex w-full justify-between gap-2 text-xs sm:text-sm">
        <div>
          <span className="font-medium">{t("games.memory.moves")}:</span> {moves}
        </div>
        <div>
          <span className="font-medium">{t("games.memory.matched")}:</span>{" "}
          {matchedPairs} / {cards.length / 2}
        </div>
        <div>
          <span className="font-medium">{t("games.memory.time")}:</span>{" "}
          {gameOver
            ? formatTime(endTime - startTime)
            : formatTime(Date.now() - startTime)}
        </div>
      </div>

      <div
        className="mx-auto grid w-full max-w-md min-h-0 flex-1 content-center gap-2"
        style={{ gridTemplateColumns: `repeat(${getColumnCount()}, minmax(0, 1fr))` }}
      >
        {cards.map((card, index) => (
          <button
            key={index}
            className={cn(
              "aspect-square max-h-[min(22vw,5.5rem)] w-full justify-self-center rounded-lg transition-all duration-300 sm:max-h-none",
              card.isFlipped || card.isMatched || flippedCards.includes(index)
                ? `${card.color} shadow-md`
                : "bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600",
              card.isMatched && "opacity-60"
            )}
            onClick={() => flipCard(index)}
            disabled={card.isMatched || gameOver}
          />
        ))}
      </div>

      {gameOver && (
        <div className="mt-4 text-center sm:mt-6">
          <p className="mb-2 text-base font-medium text-green-500 sm:text-lg">
            {t("games.memory.complete")}
          </p>
          <p className="mb-4 text-sm">
            {t("games.memory.completeStats", {
              moves,
              time: formatTime(endTime - startTime),
            })}
          </p>
          <Button onClick={initializeGame}>{t("games.ticTacToe.restart")}</Button>
        </div>
      )}
    </div>
  );
}
